import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { Address, Order } from 'depoto-core/src/entities'
import { CoreService, NotificationService } from '../../../services'
import { copyAddressForOrder, SchemaUtil } from '../../../utils'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-modal-move-order-items',
  templateUrl: './modal-move-order-items.component.html',
  styleUrls: ['./modal-move-order-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalMoveOrderItemsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('modal') childModal: ModalContainerComponent
  @Input() order: Order
  @Input() itemsFromParent: any = []
  @Input() selectedOrder: Order | Partial<Order> | any
  newOrder: Order
  loading = true
  operationOptions: string[] = ['move', 'copy']
  targetOptions: string[] = ['intoNewOrder', 'intoExistingOrder']
  selectedTargetOption = 'intoNewOrder'
  selectedOperationOption
  orderNumber: number

  searchText: string

  searchedOrders: any[] = []
  isOrderSelected = false

  public searchPhrase = ''

  @Output() orderReload: EventEmitter<any> = new EventEmitter()
  @Output() setCheckboxes: EventEmitter<any> = new EventEmitter()

  newCreatedOrder: Order

  carriers = []
  checkouts = []
  vats = []
  payments = []
  suppliers = []
  currencies = []
  depots = []

  items = []

  itemsMoved = false
  private isOriginOrderChanged = false
  private modalHiddenSubscription?: { unsubscribe(): void }

  targetOrder: Order | Partial<Order> | any

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public core: CoreService,
    private translateService: TranslateService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    if (this.order.status.id === 'bill') {
      this.targetOptions.push('corrective.tax.document')
    }
  }

  ngAfterViewInit(): void {
    // Covers every way of closing (close button, backdrop, Esc).
    this.modalHiddenSubscription = this.childModal.childModal.onHidden.subscribe(() => {
      if (!this.isOriginOrderChanged) return
      this.isOriginOrderChanged = false
      this.orderReload.emit()
    })
  }

  ngOnDestroy(): void {
    this.modalHiddenSubscription?.unsubscribe()
  }

  public showChildModal() {
    if (this.itemsFromParent?.length < 1) {
      alert(this.translateService.instant('modal-move-order-items.select.at.least.one.item'))
      return
    }
    this.items = JSON.parse(JSON.stringify(this.itemsFromParent))
    this.setDefaultValues()
    this.loading = false
    this.childModal.showChildModal()
  }
  negateSigns() {
    this.items = this.getNormalizedItemsForSelectedTarget()
  }
  maxValue(item): number {
    const originItemWithQuantity = this.itemsFromParent.find(i => i.id === item.id)
    return originItemWithQuantity?.quantity
  }

  public hideChildModal() {
    // this.setCheckboxes.emit()
    this.childModal.hideChildModal()
  }

  setDefaultValues(): void {
    this.newOrder = this.order
    this.targetOrder = undefined
    this.newCreatedOrder = undefined
    this.selectedTargetOption = 'intoNewOrder'
    this.selectedOperationOption = this.isCopyOnly ? 'copy' : 'move'
    this.orderNumber = undefined
    this.searchText = ''
    this.searchedOrders = []
    this.selectedOrder = undefined
    this.isOrderSelected = false
    this.itemsMoved = false
    this.searchPhrase = ''

    this.carriers = []
    this.checkouts = []
    this.vats = []
    this.payments = []
    this.suppliers = []
    this.currencies = []
    this.depots = []
  }

  async save() {
    if (this.selectedTargetOption === 'intoExistingOrder' && !this.selectedOrder) {
      alert(this.translateService.instant('item.choose'))
      return
    }
    this.loading = true
    this.items = this.getNormalizedItemsForSelectedTarget()
    const isMove = this.selectedOperationOption === 'move' && !this.isCopyOnly

    try {
      // The target is saved first, so a failure there leaves the source order untouched.
      const isTargetSaved =
        this.selectedTargetOption === 'intoExistingOrder'
          ? await this.addItemsToExistingOrder()
          : await this.createOrderWithItems(this.selectedTargetOption === 'corrective.tax.document')
      if (!isTargetSaved) {
        this.notificationService.error(this.translateService.instant('modal-move-order-items.target.save.failed'))
        return
      }

      if (isMove) {
        // The order page is reloaded once the modal closes (see ngAfterViewInit). Reloading now
        // would re-render the order form and destroy this modal before the result is shown.
        this.isOriginOrderChanged = true
        if (!(await this.removeMovedItemsFromOriginOrder())) {
          this.notificationService.error(this.translateService.instant('modal-move-order-items.origin.save.failed'))
        }
      }

      this.setCheckboxes.emit()
      this.itemsMoved = true
    } finally {
      this.loading = false
    }
  }

  private async addItemsToExistingOrder(): Promise<boolean> {
    const target = await this.loadOrder(this.selectedOrder.id)
    if (!target) return false
    this.selectedOrder = target
    target.items = target.items ?? []
    const shouldRecalculatePrice =
      !!this.order.currency?.id && !!target.currency?.id && this.order.currency.id !== target.currency.id

    for (const item of this.items) {
      const itemOnTarget = target.items.find(
        targetItem =>
          (item?.product?.id > 0 && item.product.id === targetItem?.product?.id) ||
          (!item?.product?.id && !targetItem?.product?.id && this.areObjectsEqual(item, targetItem))
      )
      if (itemOnTarget) {
        const previousPriceAll = this.getPriceWithSale(itemOnTarget) * Number(itemOnTarget.quantity)
        itemOnTarget.quantity = Number(itemOnTarget.quantity) + Number(item.quantity)
        itemOnTarget.priceAll = this.getPriceWithSale(itemOnTarget) * itemOnTarget.quantity
        if (itemOnTarget.product) itemOnTarget.product.orderQuantity = itemOnTarget.quantity
        this.addToPaymentAmount(target, itemOnTarget.priceAll - previousPriceAll)
        continue
      }

      // A new row on the target: the source row's id must not be sent with it.
      const newItem = { ...item, id: undefined }
      if (newItem.product?.id) newItem.product = { ...newItem.product, orderQuantity: newItem.quantity }
      if (shouldRecalculatePrice) {
        newItem.price = this.recalculatePrice(newItem.price, target.currency, this.order.currency)
        newItem.priceAll = this.getPriceWithSale(newItem) * Number(newItem.quantity)
      }
      this.addToPaymentAmount(target, newItem.priceAll)
      target.items.push(newItem)
    }

    try {
      // selectedOrder is the reloaded target (typed loosely, like before)
      const result = await this.core.services.order.updateOrderPart(this.selectedOrder, SchemaUtil.orderItems)
      if (!(result?.id > 0)) return false
      this.targetOrder = new Order(result)
      return true
    } catch (err) {
      console.error(err)
      return false
    }
  }

  private addToPaymentAmount(order, amount: number): void {
    if (order.paymentItems?.length) {
      order.paymentItems[0].amount = Number(order.paymentItems[0].amount) + (Number(amount) || 0)
    }
  }

  areObjectsEqual(item, itemOnSelectedOrder): boolean {
    //expiration date ?
    // item.moves === itemOnSelectedOrder.moves &&
    // item.clearanceItems === itemOnSelectedOrder.clearanceItems &&
    // item.quantityUnavailable === itemOnSelectedOrder.quantityUnavailable &&
    // item.uuid === itemOnSelectedOrder.uuid &&
    return (
      item.code === itemOnSelectedOrder.code &&
      item.ean === itemOnSelectedOrder.ean &&
      item.isForSubsequentSettlement === itemOnSelectedOrder.isForSubsequentSettlement &&
      item.name === itemOnSelectedOrder.name &&
      item.note === itemOnSelectedOrder.note &&
      item.packed === itemOnSelectedOrder.packed &&
      item.picked === itemOnSelectedOrder.picked &&
      item.price === itemOnSelectedOrder.price &&
      item.priceWithoutVat === itemOnSelectedOrder.priceWithoutVat &&
      item.sale === itemOnSelectedOrder.sale &&
      item.serial === itemOnSelectedOrder.serial &&
      item.voucher?.id === itemOnSelectedOrder.voucher?.id &&
      item.vat?.id === itemOnSelectedOrder.vat?.id
    )
  }

  private async loadOrder(id: number): Promise<Order | undefined> {
    try {
      const order = await this.core.services.order.getById(id, SchemaUtil.orderItems)
      return order?.id ? new Order(order) : undefined
    } catch (e) {
      console.warn(e)
      return undefined
    }
  }

  async searchForOrder() {
    this.loading = true
    const pageNo = 1
    const sortProp = 'id'
    const sortDir = 'desc'
    this.searchedOrders = await this.core.services.order
      .getList(
        {
          page: pageNo,
          sort: sortProp,
          direction: sortDir,
          filters: { fulltext: this.searchPhrase, status: 'reservation' },
        },
        SchemaUtil.orderItems
      )
      .then(result => {
        return result.items
      })
    this.loading = false
  }

  orderSelected(order) {
    this.selectedOrder = order
  }

  orderUnselected() {
    this.selectedOrder = undefined
  }

  // Works on a copy of the source order: the order page keeps showing its loaded state until the
  // modal closes and the page reloads.
  private async removeMovedItemsFromOriginOrder(): Promise<boolean> {
    const remainingItems = []
    const deletedItems = []
    for (const itemOnOriginOrder of this.order.items ?? []) {
      const movedItem = this.items.find(item => this.isSameItem(item, itemOnOriginOrder))
      if (!movedItem) {
        remainingItems.push(itemOnOriginOrder)
      } else if (Number(itemOnOriginOrder.quantity) === Number(movedItem.quantity)) {
        if (itemOnOriginOrder.id) deletedItems.push(itemOnOriginOrder)
      } else {
        const quantity = Number(itemOnOriginOrder.quantity) - Number(movedItem.quantity)
        remainingItems.push({
          ...itemOnOriginOrder,
          quantity,
          priceAll: this.getPriceWithSale(itemOnOriginOrder) * quantity,
        })
      }
    }
    const originOrder = {
      ...this.order,
      items: remainingItems,
      paymentItems: (this.order.paymentItems ?? []).map(paymentItem => ({ ...paymentItem })),
    }
    this.refreshPaymentAmount(originOrder)

    try {
      for (const item of deletedItems) {
        await this.core.services.order.deleteOrderItem(item)
      }
      const keepReservation = true
      const result = await this.core.services.order.update(originOrder, keepReservation, undefined, undefined, {
        id: null,
      })
      return result?.id > 0
    } catch (err) {
      console.error(err)
      return false
    }
  }

  private isSameItem(movedItem, itemOnOriginOrder): boolean {
    return movedItem.id
      ? movedItem.id === itemOnOriginOrder.id
      : !!movedItem.uuid && movedItem.uuid === itemOnOriginOrder.uuid
  }

  public refreshPaymentAmount(order): void {
    if (order.paymentItems && order.paymentItems.length === 1) {
      const itemPrices = order.items.map(item => item.priceAll)
      order.paymentItems[0].amount = itemPrices.reduce((a, b) => a + b, 0)
    }
  }

  async createOrderWithItems(isODD = false): Promise<boolean> {
    const newOrder = JSON.parse(JSON.stringify(this.order))
    const normalizedItems = this.getNormalizedItemsForSelectedTarget()

    //set some properties to null and set items
    newOrder.isPaid = false
    newOrder.id = null
    newOrder.dateCreated = null
    newOrder.items = normalizedItems
    newOrder.reservationNumber = null
    newOrder.billNumber = null

    await this.refreshPaymentAmount(newOrder)

    newOrder.items.forEach(it => {
      delete it.id
    })
    if (isODD) {
      newOrder.relatedParent = { id: this.order.id }
      newOrder.status = { id: 'bill' }
    } else {
      newOrder.status = { id: 'reservation' }
    }
    let result
    try {
      await this.createAddressCopiesForNewOrder(newOrder)
      result = await this.core.services.order.create(newOrder)
    } catch (err) {
      console.error(err)
      return false
    }
    if (!(result?.id > 0)) return false
    this.targetOrder = result
    this.newOrder = (await this.loadOrder(result.id)) ?? this.newOrder
    if (this.newOrder?.id === result.id) this.targetOrder = this.newOrder
    return true
  }

  // the new order must not reuse the source order's address rows,
  // otherwise editing one order's address overwrites the other
  private async createAddressCopiesForNewOrder(newOrder): Promise<void> {
    const sourceInvoiceAddress = newOrder.invoiceAddress
    const sourceShippingAddress = newOrder.shippingAddress
    const isShippingAddressSameAsInvoice =
      !!sourceInvoiceAddress?.id && sourceInvoiceAddress.id === sourceShippingAddress?.id

    newOrder.invoiceAddress = await this.createAddressCopy(sourceInvoiceAddress, true, newOrder.customer)
    newOrder.shippingAddress = isShippingAddressSameAsInvoice
      ? newOrder.invoiceAddress
      : await this.createAddressCopy(sourceShippingAddress, false, newOrder.customer)
  }

  private async createAddressCopy(address, isBilling: boolean, customer): Promise<Address | null> {
    if (!address?.id && !address?.country) {
      return null
    }
    const addressCopy = copyAddressForOrder(address, isBilling)
    // TODO CORE MUTATION
    const createdAddress = await this.core.services.address.create(addressCopy, customer?.id ? customer : null)
    return new Address({ ...addressCopy, id: createdAddress.id })
  }

  recalculatePrice(price, newCurrency, originalCurrency?) {
    let recalculatedPrice: number
    if (originalCurrency.id === 'CZK') {
      recalculatedPrice = Number((price * newCurrency.ratio).toFixed(2))
    } else if (newCurrency.id === 'CZK') {
      recalculatedPrice = Number((price / originalCurrency.ratio).toFixed(2))
    } else if (originalCurrency.id !== 'CZK' && newCurrency.id !== 'CZK') {
      const priceInCzk = Number(price / originalCurrency.ratio)
      recalculatedPrice = Number((priceInCzk * newCurrency.ratio).toFixed(2))
    }
    return recalculatedPrice
  }

  private getNormalizedItemsForSelectedTarget(): any[] {
    return this.items.map(item => {
      const originItem = this.getOriginItem(item)
      const quantity = Math.abs(Number(item.quantity) || 0)
      const targetLineSign = this.getTargetLineSign(item, originItem)
      const unitPriceSign = this.getSign(this.getPriceWithSale(item))
      const quantitySign = unitPriceSign ? targetLineSign * unitPriceSign : targetLineSign
      const signedQuantity = quantitySign * quantity

      return {
        ...item,
        quantity: signedQuantity,
        priceAll: this.getSignedPriceAll(item, originItem, quantity, signedQuantity, targetLineSign),
      }
    })
  }

  private getOriginItem(item) {
    return this.itemsFromParent.find(
      i => (item.id && i.id === item.id) || (!item.id && item.uuid && i.uuid === item.uuid)
    )
  }

  private getTargetLineSign(item, originItem): number {
    const sourceItem = originItem || item
    const sourcePriceAllSign = this.getSign(Number(sourceItem.priceAll))
    const sourceCalculatedSign = this.getSign(this.getPriceWithSale(sourceItem) * Number(sourceItem.quantity || 0))
    const sourceLineSign = sourcePriceAllSign || sourceCalculatedSign || 1

    return this.selectedTargetOption === 'corrective.tax.document' ? -sourceLineSign : sourceLineSign
  }

  private getSignedPriceAll(
    item,
    originItem,
    quantity: number,
    signedQuantity: number,
    targetLineSign: number
  ): number {
    const originQuantity = Math.abs(Number(originItem?.quantity) || 0)

    if (originItem && quantity === originQuantity) {
      return targetLineSign * Math.abs(Number(originItem.priceAll) || 0)
    }

    return this.getPriceWithSale(item) * signedQuantity
  }

  private getPriceWithSale(item): number {
    const price = Number(item.price) || 0
    const sale = Number(item.sale) || 0

    return sale ? (price / 100) * (100 - sale) : price
  }

  private getSign(value: number): number {
    if (value < 0) return -1
    if (value > 0) return 1
    return 0
  }

  // bills and canceled orders can't be updated, so items can only be copied out of them
  get isCopyOnly(): boolean {
    return this.order.status?.id === 'bill' || this.order.processStatus?.id === 'cancelled'
  }

  getTitle(): string {
    if (this.isCopyOnly) return 'selected.items.copy'
    else return 'selected.items.move'
  }

  openUpdatedOrder(id) {
    const url = '/order/'.concat(id.toString())
    window.open(url)
  }
}
